import React from 'react'
import dayjs from 'dayjs'
import Accordion from '@mui/material/Accordion'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import AccordionSummary from '@mui/material/AccordionSummary'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import AccordionDetails from '@mui/material/AccordionDetails'
import Avatar from '@mui/material/Avatar'

export type RoutePoint = {
  label?: string
  arrivalTime: string
  isEdge: boolean
}

export type RouteSummaryData = {
  memberName: string
  pointsCount: number
  totalTime: number
  serviceTime: number
  travelTime: number
  totalDistance: number
  points: RoutePoint[]
}

type Props = {
  index: number
  data: RouteSummaryData
  color: string
  textColor: string
  expanded: boolean
  onToggle: (index: number, isExpanded: boolean) => void
}

const secondsToHhmm = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const metersToKm = (meters: number): string => {
  return (meters / 1000.0).toFixed(2)
}

const RouteSummaryItem = React.memo(({ index, data, color, textColor, expanded, onToggle }: Props) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', pt: 2, pb: 2, gap: 1 }}>
    <Typography variant='h6' sx={{ color, px: 2 }}>{data.memberName}</Typography>
    <Box sx={{ display: 'flex', px: 2, gap: 8 }}>
      <Typography variant='body1'>
        <b>Atividades:</b> {data.pointsCount}
      </Typography>
      <Typography variant='body1'>
        <b>Tempo total:</b> {secondsToHhmm(data.totalTime)}
      </Typography>
    </Box>
    <Accordion expanded={expanded} onChange={(_, isExpanded) => onToggle(index, isExpanded)} sx={{ '::before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Typography variant='body1' sx={{ fontWeight: 'bold' }} color='text.secondary'>
          Detalhamento da rota
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ maxHeight: 300, overflowY: 'auto' }}>
        <Typography variant='subtitle1' color='text.secondary'>
          <b>Duração das atividades:</b> {secondsToHhmm(data.serviceTime)}
        </Typography>
        <Typography variant='subtitle1' color='text.secondary'>
          <b>Tempo em deslocamento:</b> {secondsToHhmm(data.travelTime)}
        </Typography>
        <Typography variant='subtitle1' color='text.secondary'>
          <b>Distância total:</b> {metersToKm(data.totalDistance)}km
        </Typography>
        {data.points.map((point, pointIndex) => (
          <Box key={pointIndex} sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
            <Avatar sx={{ bgcolor: color, color: textColor, width: 24, height: 24, fontSize: 12 }}>
              {point.isEdge ? ' ' : pointIndex}
            </Avatar>
            <Box>
              <Typography variant='body1' color='text.secondary'>
                {point.label}
              </Typography>
              <Typography variant='subtitle1' color='text.secondary'>
                <b>Previsão de chegada:</b> {dayjs(point.arrivalTime).isValid() ? dayjs(point.arrivalTime).format('HH:mm') : point.arrivalTime}
              </Typography>
            </Box>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  </Box>
))

export default RouteSummaryItem
